import { useEffect } from 'react';

import { trackUpdateAutoshipAddress, trackViewUpdateAutoshipAddressModal } from '@libs/analytics';

import { InfoModal } from '../../info-modal';

type ConfirmAutoshipDefaultAddressChangeModalProps = {
    isActive: boolean;
    isFraudValidationNeeded?: boolean;
    onClose: () => void;
    onKeepExistingAddress: () => void;
    onUpdateAddress: () => void;
    loading?: 'keep' | 'update';
};

const KEEP_BUTTON_TEXT = 'No, keep existing address';
const UPDATE_BUTTON_TEXT = 'Yes, update address';
const SITE_SECTION = 'my account | addresses';

export function ConfirmAutoshipDefaultAddressChangeModal({
    isActive,
    isFraudValidationNeeded = false,
    onClose,
    onKeepExistingAddress,
    onUpdateAddress,
    loading,
}: Readonly<ConfirmAutoshipDefaultAddressChangeModalProps>) {
    useEffect(() => {
        if (isActive) trackViewUpdateAutoshipAddressModal(SITE_SECTION);
    }, [isActive]);

    const handleClose = () => {
        trackUpdateAutoshipAddress({
            linkText: 'close',
            siteSection: SITE_SECTION,
        });

        onClose();
    };

    const isKeepLoading = loading === 'keep' && !isFraudValidationNeeded;
    const isUpdateLoading = loading === 'update' || (isFraudValidationNeeded && loading === 'keep');

    return (
        <InfoModal
            isActive={isActive}
            title={isFraudValidationNeeded ? 'Update address' : 'Update Autoship address'}
            onClose={handleClose}
            closeButtonTestId="update-autoship-modal-close-btn"
            footerTestId="update-autoship-modal-footer"
            secondaryAction={{
                text: isFraudValidationNeeded ? 'Cancel' : KEEP_BUTTON_TEXT,
                onClick: () => {
                    trackUpdateAutoshipAddress({
                        linkText: KEEP_BUTTON_TEXT.toLowerCase(),
                        siteSection: SITE_SECTION,
                    });

                    onKeepExistingAddress();
                },
                loading: isKeepLoading,
            }}
            primaryAction={{
                text: isFraudValidationNeeded ? 'Save new address' : UPDATE_BUTTON_TEXT,
                onClick: () => {
                    trackUpdateAutoshipAddress({
                        linkText: UPDATE_BUTTON_TEXT.toLowerCase(),
                        siteSection: SITE_SECTION,
                    });
                    onUpdateAddress();
                },
                loading: isUpdateLoading,
            }}
            text={
                isFraudValidationNeeded
                    ? 'Updating your address will save it as a new address in your account. Existing Autoship orders will continue using their current shipping address.'
                    : 'Your previous default address is linked to one or more Autoship orders. Would you like to use your new default address for those Autoship orders?'
            }
        />
    );
}
